import { useCallback, useMemo } from 'react';
import { uniqBy } from 'lodash-es';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import {
  FACILITY_PLACE_TYPES,
  labelFacilityPlaces,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils/locations';
import { getFullName } from '@/utils/helpers';
import {
  AviaLegTypes,
  GroundLegTypes,
  HubRouteLocation,
  LegLocationTypeEnum,
  PredefinedPlaceType,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { CaseFacility } from '@/api/apis/case';
import { RouteSegmentType } from '@/types/route';

type UseSegmentLocationsProps = {
  segments: RouteSegmentType[];
  donorHospital: CaseFacility;
  txCenter?: CaseFacility;
  legs?: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
};

export const useSegmentLocations = ({ segments, donorHospital, txCenter, legs }: UseSegmentLocationsProps) => {
  const { ClinicalSpecialist: csList, Surgeon: sList, OCSConsole: devicesList } = useRouteResources();
  const { getLocationById } = useRouteLocations();
  const { locations: logisticsLocations } = useLogisticsData();

  // Non-case DH/TXC are allowed in custom segments only if they already exist in the logistics
  const logisticsFacilityPlaces = useMemo<PredefinedPlaceType[]>(
    () =>
      logisticsLocations
        .filter((l) => FACILITY_PLACE_TYPES.includes(l.locationType))
        .map((l) => ({ id: l.id, type: l.locationType })),
    [logisticsLocations],
  );

  // DH/TXC already used as locations in main segment legs stay selectable for new legs of that segment
  const facilityLocationIdsBySegmentType = useMemo(() => {
    const result = new Map<SegmentTypeEnum, string[]>();

    [...(legs?.values() || [])].flat().forEach((leg) => {
      const locationIds = [leg.departureLocation, leg.arrivalLocation]
        .filter((l) => FACILITY_PLACE_TYPES.includes(l.locationType))
        .map((l) => l.locationId);

      result.set(leg.segmentType, [...(result.get(leg.segmentType) || []), ...locationIds]);
    });

    return result;
  }, [legs]);

  // DH/TXC already used by the leg have to be available in Edit/View mode
  const getIncludedFacilityPlaces = useCallback(
    (locationIds: string[] = []) =>
      locationIds.flatMap<PredefinedPlaceType>((id) => {
        const location = getLocationById(id);

        if (!location || !FACILITY_PLACE_TYPES.includes(location.locationType)) return [];

        return [{ id: location.id, type: location.locationType }];
      }),
    [getLocationById],
  );

  const getCustomSegmentLocation = useCallback(() => {
    const dryRunSegment = segments.find((s) => s.type === SegmentTypeEnum.dhToHub);
    const startHubSegment = segments.find((s) => s.type === SegmentTypeEnum.hospital);
    const endHubSegment = segments.find((s) => s.type === SegmentTypeEnum.hub);

    const csHomeLocations = csList
      .map((cs) =>
        cs.data?.homeLocation
          ? {
              id: cs.data.homeLocation.id,
              type: LegLocationTypeEnum.homeResidence,
              label: `Home CS (${getFullName(cs.data.firstName, cs.data.lastName)})`,
            }
          : null,
      )
      .filter((i) => !!i);

    const csBaseLocations = csList
      .map((cs) =>
        cs.data?.baseLocation
          ? {
              id: cs.data.baseLocation.id,
              type: LegLocationTypeEnum.hub,
              label: `Hub ${cs.data.baseLocation.code}`,
            }
          : null,
      )
      .filter((i) => !!i);

    const sLocations = sList
      .map((s) =>
        s.data?.homeLocation
          ? {
              id: s.data.homeLocation.id,
              type: LegLocationTypeEnum.homeResidence,
              label: `Home RS (${getFullName(s.data.firstName, s.data.lastName)})`,
            }
          : null,
      )
      .filter((i) => !!i);

    const dLocations = devicesList
      .map((d) =>
        d.data?.hub
          ? {
              id: d.data.hub.id,
              type: LegLocationTypeEnum.hub,
              label: `Hub ${d.data.hub.code}`,
            }
          : null,
      )
      .filter((i) => !!i);

    const places = [
      ...csHomeLocations,
      ...sLocations,
      ...dLocations,
      ...csBaseLocations,
      ...(startHubSegment?.startLocation.locationId
        ? [
            {
              id: startHubSegment.startLocation.locationId,
              type: LegLocationTypeEnum.hub,
              label: `Hub ${(getLocationById(startHubSegment.startLocation.locationId) as HubRouteLocation).code}`,
            },
          ]
        : []),
      ...(endHubSegment?.endLocation.locationId
        ? [
            {
              id: endHubSegment.endLocation.locationId,
              type: LegLocationTypeEnum.hub,
              label: `Hub ${(getLocationById(endHubSegment.endLocation.locationId) as HubRouteLocation).code}`,
            },
          ]
        : []),
      ...(dryRunSegment?.endLocation.locationId
        ? [
            {
              id: dryRunSegment.endLocation.locationId,
              type: LegLocationTypeEnum.hub,
              label: `Hub ${(getLocationById(dryRunSegment.endLocation.locationId) as HubRouteLocation).code}`,
            },
          ]
        : []),
      {
        id: donorHospital.id,
        type: LegLocationTypeEnum.hospital,
        label: 'Donor Hospital',
      },
      ...(txCenter
        ? [
            {
              id: txCenter.id,
              type: LegLocationTypeEnum.txcenter,
              label: 'TX Center',
            },
          ]
        : []),
      ...logisticsFacilityPlaces,
    ] as PredefinedPlaceType[];

    return places;
  }, [segments, csList, sList, devicesList, getLocationById, donorHospital.id, txCenter, logisticsFacilityPlaces]);

  return useCallback(
    (segmentType: string, isNewSegment?: boolean, includeLocationIds?: string[]) => {
      const includedPlaces = getIncludedFacilityPlaces(includeLocationIds);
      const finalizePlaces = (places: PredefinedPlaceType[]) =>
        labelFacilityPlaces(
          uniqBy([...places, ...includedPlaces], 'id').filter((p) => !!p.id),
          getLocationById,
        );

      const currentSegment = segments.find((s) => s.type === segmentType);

      const isCustomType = segmentType === SegmentTypeEnum.custom || !segmentType || isNewSegment;

      if (isCustomType) {
        return finalizePlaces(getCustomSegmentLocation());
      }

      const showStartHub = segmentType === SegmentTypeEnum.hospital;
      const showEndHub = segmentType === SegmentTypeEnum.hub;
      const showDryRunHub = segmentType === SegmentTypeEnum.dhToHub;
      const showHospital =
        segmentType === SegmentTypeEnum.txcenter ||
        segmentType === SegmentTypeEnum.hospital ||
        segmentType === SegmentTypeEnum.dhToHub;
      const showTxCenter = segmentType === SegmentTypeEnum.txcenter || segmentType === SegmentTypeEnum.hub;

      const places = [
        ...(showStartHub && currentSegment
          ? [
              {
                id: currentSegment.startLocation.locationId,
                type: LegLocationTypeEnum.hub,
                label: `Hub`,
              },
            ]
          : []),
        ...(showEndHub && currentSegment
          ? [
              {
                id: currentSegment.endLocation.locationId,
                type: LegLocationTypeEnum.hub,
                label: `Hub`,
              },
            ]
          : []),
        ...(showDryRunHub && currentSegment
          ? [
              {
                id: currentSegment.endLocation.locationId,
                type: LegLocationTypeEnum.hub,
                label: `Hub`,
              },
            ]
          : []),
        ...(showHospital
          ? [
              {
                id: donorHospital.id,
                type: LegLocationTypeEnum.hospital,
                label: 'Donor Hospital',
              },
            ]
          : []),
        ...(showTxCenter && txCenter
          ? [
              {
                id: txCenter.id,
                type: LegLocationTypeEnum.txcenter,
                label: 'TX Center',
              },
            ]
          : []),
      ] as PredefinedPlaceType[];

      const segmentFacilityPlaces = getIncludedFacilityPlaces(
        facilityLocationIdsBySegmentType.get(segmentType as SegmentTypeEnum),
      );

      return finalizePlaces([...places, ...segmentFacilityPlaces]);
    },
    [
      getCustomSegmentLocation,
      getIncludedFacilityPlaces,
      getLocationById,
      facilityLocationIdsBySegmentType,
      donorHospital,
      segments,
      txCenter,
    ],
  );
};
